import { ExecutionData } from "../../../../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { StatementRuleConfiguration } from "../../../../../rule-engine-core/Rule/Statement/StatementRuleConfiguration";
import type {
    DemoRuleIntegrationAdapter,
    RuleConfigFactoryContext,
    RuleDemoDataProjectionPort,
    RuleEvaluatorPort,
    RuleIntegrationStatus,
    RulePolicyBinding,
    RuleRowMapperPort,
    RuleSimplifiedLogicEditorPort,
    RuleSummaryPresenterPort,
} from "../../ruleIntegrationTypes";
import { identityRuleRowMapper } from "../../ruleIntegrationTypes";
import type { RuleSuggestionCommandPort } from "../../../application/ports/RuleSuggestionCommandPort";
import { withExecutableRuntimeEvidence } from "../runtimeEvidence";
import { statementRuleStorageCodec } from "../storageCodecs";

export const STATEMENT_RULE_EXECUTABLE_TYPE = "StatementRuleConfiguration";

export type StatementRuleBooleanRunResult =
    | {
          appGluePassed: boolean;
          appGlueRuntimeError?: undefined;
      }
    | {
          appGluePassed: false;
          appGlueRuntimeError: string;
      };

export type CreateStatementRuleIntegrationAdapterParams<DemoRow, RuntimeRow = DemoRow> = {
    ruleId: string;
    status?: Exclude<RuleIntegrationStatus, "demo_only">;
    policyBinding?: RulePolicyBinding;
    createConfig: (context?: RuleConfigFactoryContext) => StatementRuleConfiguration;
    rowMapper?: RuleRowMapperPort<DemoRow, RuntimeRow>;
    evaluator: RuleEvaluatorPort<StatementRuleConfiguration, RuntimeRow>;
    summaryPresenter: RuleSummaryPresenterPort<StatementRuleConfiguration>;
    simplifiedLogicEditor?: RuleSimplifiedLogicEditorPort<StatementRuleConfiguration>;
    demoDataProjection?: RuleDemoDataProjectionPort<StatementRuleConfiguration>;
    suggestionCommands?: RuleSuggestionCommandPort<StatementRuleConfiguration>;
};

export const runStatementRuleBoolean = (
    config: StatementRuleConfiguration,
    executionData: ExecutionData,
): StatementRuleBooleanRunResult => {
    try {
        return {
            appGluePassed: config.run(executionData) === true,
        };
    } catch (error) {
        return {
            appGluePassed: false,
            appGlueRuntimeError: error instanceof Error ? error.message : "AppGlue expression evaluation failed.",
        };
    }
};

export const createStatementRuleIntegrationAdapter = <DemoRow, RuntimeRow = DemoRow>({
    ruleId,
    status = "native_appglue",
    policyBinding,
    createConfig,
    rowMapper,
    evaluator,
    summaryPresenter,
    simplifiedLogicEditor,
    demoDataProjection,
    suggestionCommands,
}: CreateStatementRuleIntegrationAdapterParams<DemoRow, RuntimeRow>): DemoRuleIntegrationAdapter<
    StatementRuleConfiguration,
    DemoRow,
    RuntimeRow
> => ({
    ruleId,
    status,
    appGlueExecutableType: STATEMENT_RULE_EXECUTABLE_TYPE,
    editorSurface: "native_appglue_editor",
    policyBinding,
    ports: {
        configFactory: { createConfig },
        storageCodec: statementRuleStorageCodec(),
        rowMapper: rowMapper ?? (identityRuleRowMapper<DemoRow>() as unknown as RuleRowMapperPort<DemoRow, RuntimeRow>),
        evaluator: withExecutableRuntimeEvidence(STATEMENT_RULE_EXECUTABLE_TYPE, evaluator),
        summaryPresenter,
        simplifiedLogicEditor,
        demoDataProjection,
        suggestionCommands:
            suggestionCommands ??
            (simplifiedLogicEditor
                ? {
                      supportsCommand: (command, context) =>
                          command.type === "updateStatementExpression" &&
                          command.ruleId === ruleId &&
                          command.executableType === STATEMENT_RULE_EXECUTABLE_TYPE &&
                          context.ruleId === ruleId &&
                          context.executableType === STATEMENT_RULE_EXECUTABLE_TYPE &&
                          context.config instanceof StatementRuleConfiguration,
                      applyCommand: (command, config) => {
                          if (
                              command.type !== "updateStatementExpression" ||
                              command.ruleId !== ruleId ||
                              command.executableType !== STATEMENT_RULE_EXECUTABLE_TYPE
                          ) {
                              return null;
                          }

                          return simplifiedLogicEditor.applyEdit(config, command.edit);
                      },
                  }
                : undefined),
    },
});
